import React, {CSSProperties, forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'

import moment from 'moment'
import _ from 'lodash'
import {Options, RRule, Weekday} from 'rrule'

import languages from '../../../../server/languages.js'
import t, {getLocale} from '../../../helpers/Languages.js'

import P from '../typography/P.js'
import FormRow from './FormRow.js'
import Option from './Option.js'
import Select from './Select.js'

const rruleDays = {
    0: RRule.MO,
    1: RRule.TU,
    2: RRule.WE,
    3: RRule.TH,
    4: RRule.FR,
    5: RRule.SA,
    6: RRule.SU
}

const days = {
    DUTCH: {
        0: 'M',
        1: 'D',
        2: 'W',
        3: 'D',
        4: 'V',
        5: 'Z',
        6: 'Z'
    },
    ENGLISH: {
        0: 'M',
        1: 'T',
        2: 'W',
        3: 'T',
        4: 'F',
        5: 'S',
        6: 'S'
    }
}

// The rule being edited: weekly rules have byweekday as day numbers (0 is Monday), monthly ones as a Weekday with n
// (the first, second... -1 the last of the month)
type RuleOptions = Partial<Omit<Options, 'byweekday'>> & {byweekday?: Weekday | number[] | number}

export interface RecurrenceProps {
    /** The rule as an RRule string, the picker is only shown when there is one */
    rrule?: string
    /** The start date of the rule (dtstart, at 12:00): a change emits the rule again with it */
    date?: string | Date
    /** Called with the new rule string as event.target.value */
    onChange?: (event: {target: {value: string}}) => void
    /** 'subscription': monthly, quarterly or yearly, starting today, without days */
    type?: string
    /** Shows the rule as a text instead of the picker */
    disabled?: boolean
    /** 'column' (default): the days or the day of the month below the frequency, 'row': next to it */
    direction?: 'column' | 'row'
    style?: CSSProperties
    /** Style of the icon in front of the text when disabled */
    iconStyle?: CSSProperties
}

/** The imperative API of Recurrence, the type of its ref */
export interface RecurrenceRef {
    /** The rule as an RRule string with dtstart set to the date prop (at 12:00) */
    init: () => string
}

// The state of the picker from the rrule and type props
function initialState(rruleProp: string | undefined, type: string | undefined) {
    let rrule: RuleOptions

    // if prop is present, parse it back into fitting component state
    if (rruleProp) {
        rrule = RRule.fromString(rruleProp).origOptions as RuleOptions

        if (rrule.byweekday) {
            if (Array.isArray(rrule.byweekday) && rrule.byweekday.length === 1) {
                rrule.byweekday = rrule.byweekday[0]
            } else {
                rrule.byweekday = (rrule.byweekday as unknown as Weekday[]).map((day) => day.weekday)
            }
        }
    } else {
        rrule = {freq: RRule.WEEKLY, interval: 1, byweekday: [0, 1, 2, 3, 4]}
    }

    if (type === 'subscription') {
        rrule = {freq: RRule.MONTHLY, interval: 1, dtstart: moment(moment().format('YYYY-MM-DD')).hour(12).toDate()}
    }

    let frequency = type === 'subscription' ? 3 : 1

    // if rrule is present, map the interval to the correct component frequency value
    if (rruleProp) {
        if (rrule.interval === 4) {
            frequency = 6
        } else if (rrule.interval === 12) {
            frequency = 5
        } else if (rrule.interval === 3) {
            if (rrule.freq === RRule.WEEKLY) {
                frequency = 7
            } else if (rrule.freq === RRule.MONTHLY) {
                frequency = 4
            }
        } else if (rrule.interval === 1) {
            if (rrule.freq === RRule.WEEKLY) {
                frequency = 1
            } else if (rrule.freq === RRule.MONTHLY) {
                frequency = 3
            } else {
                frequency = Array.isArray(rrule.byweekday) ? 1 : 3
            }
        } else {
            frequency = rrule.interval
        }
    }

    return {frequency, rrule}
}

/**
 * Picks how often something repeats (every week up to every year, plus the days of the week or the day of the
 * month) and emits it as an RRule string. The picker reads the rrule prop only when it mounts; disabled shows the
 * rule as a text.
 */
const Recurrence = forwardRef<RecurrenceRef, RecurrenceProps>(function Recurrence(props, ref) {
    const {type, disabled, direction = 'column', style, iconStyle, date} = props
    const [state, setState] = useState(() => initialState(props.rrule, type))
    const {frequency, rrule} = state

    // init() is called from the parent with the latest state and date
    const latest = useRef({state, date})
    latest.current = {state, date}

    // Like before, the rule in the state gets the dtstart it is emitted with
    const toRuleString = (rule: RuleOptions, ruleDate: string | Date | undefined) => {
        rule.dtstart = ruleDate ? moment(ruleDate).hour(12).toDate() : rule.dtstart
        return new RRule(_.clone(rule) as Partial<Options>).toString()
    }

    const emitChange = (rule: RuleOptions, ruleFrequency: number) => {
        if (ruleFrequency) {
            props.onChange?.({target: {value: toRuleString(rule, date)}})
        } else {
            props.onChange?.({target: {value: ''}})
        }
    }

    const update = (rule: RuleOptions, ruleFrequency = frequency) => {
        setState({rrule: rule, frequency: ruleFrequency})
        emitChange(rule, ruleFrequency)
    }

    useImperativeHandle(ref, () => ({
        init: () => toRuleString(latest.current.state.rrule, latest.current.date)
    }), [])

    // A new date moves the start of the rule (not on mount)
    const prevDate = useRef(date)
    useLayoutEffect(() => {
        if (prevDate.current !== date) {
            prevDate.current = date

            if (props.rrule && date) {
                emitChange(rrule, frequency)
            }
        }
    }, [date])

    const selectRruleFreq = (event: {target: {value: string | number}}) => {
        const next = {...rrule}
        const nextFrequency = parseInt(String(event.target.value))

        if (nextFrequency === 1 || nextFrequency === 2 || nextFrequency === 6 || nextFrequency === 7) {
            next.freq = 2
            next.interval = nextFrequency === 7 ? 3 : nextFrequency === 6 ? 4 : nextFrequency

            delete next.bymonthday

            if (type !== 'subscription') {
                next.byweekday = [0, 1, 2, 3, 4]
            }
        } else if (nextFrequency === 3 || nextFrequency === 4 || nextFrequency === 5) {
            next.freq = 1
            next.interval = nextFrequency === 5 ? 12 : (nextFrequency === 4 ? 3 : 1)

            if (type === 'subscription') {
                delete next.byweekday
            } else {
                next.byweekday = rruleDays[0].nth(1)
            }
        }

        update(next, nextFrequency)
    }

    const changeRruleDays = (day: number, event: {target: {checked: boolean}}) => {
        // Normalize byweekday to always be an array of numbers
        let byweekday: unknown = rrule.byweekday

        if (!Array.isArray(byweekday)) {
            // If it's a single object with a 'weekday' property, extract the number
            if (byweekday && typeof byweekday === 'object' && (byweekday as Weekday).weekday !== undefined) {
                byweekday = [(byweekday as Weekday).weekday]
            } else if (typeof byweekday === 'number') {
                byweekday = [byweekday]
            } else {
                byweekday = []
            }
        } else {
            // If it's an array of objects, map to numbers
            byweekday = byweekday.map((d) =>
                typeof d === 'object' && d.weekday !== undefined ? d.weekday : d
            )
        }

        let weekdays = byweekday as number[]

        if (event.target.checked) {
            if (!weekdays.includes(day)) {
                weekdays.push(day)
            }
        } else {
            weekdays = weekdays.filter((d) => d !== day)
        }

        update({...rrule, byweekday: weekdays})
    }

    const changeRruleByWeekDay = (event: {target: {value: string | number}}) => {
        update({...rrule, byweekday: rruleDays[parseInt(String(event.target.value))].nth((rrule.byweekday as Weekday).n)})
    }

    const changeRruleByWeekDayN = (event: {target: {value: string | number}}) => {
        update({...rrule, byweekday: rruleDays[(rrule.byweekday as Weekday).weekday].nth(parseInt(String(event.target.value)))})
    }

    const rruleToText = () => {
        if (props.rrule) {
            const locale = getLocale()
            const language = locale === 'en' ? languages.ENGLISH : languages.DUTCH
            return `${RRule.fromString(props.rrule).toText((id) => {
                return language.rrule[id as string] || id
            }, language)}`
        }
    }

    if (disabled) {
        return (
            <>
                {props.rrule &&
                    <div style={{flex: 1, display: 'flex', alignItems: 'start', marginBottom: 12, ...style}}>
                        <i style={{marginRight: 6, fontSize: 18, ...iconStyle}} className='mdi mdi-refresh' />
                        <P>{rruleToText()} </P>
                    </div>
                }
            </>
        )
    }

    const locale = getLocale()
    const language = locale === 'en' ? 'ENGLISH' : 'DUTCH'

    return (
        <>
            {props.rrule &&
                <FormRow style={{position: 'relative', ...style}}>
                    <Select
                        label={frequency > 3 && frequency !== 6 && frequency !== 7 ? t('repeatEvery') : t('repeatsEvery')}
                        value={frequency}
                        onChange={selectRruleFreq}
                    >
                        <option value={1}>Week</option>
                        <option value={2}>2 {t('weeks')}</option>
                        <option value={7}>3 {t('weeks')}</option>
                        <option value={6}>4 {t('weeks')}</option>
                        <option value={3}>{_.capitalize(t('month'))}</option>
                        <option value={4}>{_.capitalize(t('quarter'))}</option>
                        <option value={5}>{_.capitalize(t('year'))}</option>
                    </Select>

                    {!!frequency && rrule.freq === RRule.WEEKLY && type !== 'subscription' &&
                        <div style={{
                            display: 'flex',
                            flex: 1,
                            ...(direction === 'column' ?
                                {
                                    position: 'absolute',
                                    top: 54,
                                    left: 0
                                } :
                                {
                                }),
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start'
                        }}
                        >
                            {_.keys(days[language]).map((key) => {
                                return (
                                    <Option
                                        key={key}
                                        label={days[language][key]}
                                        checked={_.includes(rrule.byweekday as number[], parseInt(key))}
                                        onChange={(event) => changeRruleDays(parseInt(key), event)}
                                    />
                                )
                            })}
                        </div>
                    }

                    {!!frequency && rrule.freq === RRule.MONTHLY && type !== 'subscription' &&

                        <FormRow style={{
                            ...(direction === 'column' ?
                                {
                                    position: 'absolute',
                                    top: 54,
                                    left: 0,
                                    width: undefined
                                } :
                                {
                                    flex: 1
                                })
                        }}
                        >
                            <Select
                                value={(rrule.byweekday as Weekday).n}
                                onChange={changeRruleByWeekDayN}
                            >
                                <option value={1}>{t('first')}</option>
                                <option value={2}>{t('second')}</option>
                                <option value={3}>{t('third')}</option>
                                <option value={4}>{t('fourth')}</option>
                                <option value={-1}>{t('last')}</option>
                            </Select>

                            <Select
                                value={(rrule.byweekday as Weekday).weekday}
                                onChange={changeRruleByWeekDay}
                                style={{textTransform: 'capitalize'}}
                            >
                                <option value={0}>{languages[language].rrule.monday}</option>
                                <option value={1}>{languages[language].rrule.tuesday}</option>
                                <option value={2}>{languages[language].rrule.wednesday}</option>
                                <option value={3}>{languages[language].rrule.thursday}</option>
                                <option value={4}>{languages[language].rrule.friday}</option>
                                <option value={5}>{languages[language].rrule.saturday}</option>
                                <option value={6}>{languages[language].rrule.sunday}</option>
                            </Select>
                        </FormRow>
                    }
                </FormRow>
            }
        </>
    )
})

export default Recurrence
